import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { QuoteSelector } from "@/components/QuoteSelector";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { SampleFieldsForm } from "./SampleFieldsForm";
import { SampleLinesEditor } from "./SampleLinesEditor";
import {
  selectBinding,
  selectSample,
  useTemplateSampleStore,
  type SampleBinding,
} from "./templateSampleStore";
import { useConfiguredSampleValues } from "./useConfiguredSampleValues";
import { collectUsedSources } from "./usedSources";

/** Shown when a picked quote loads but carries no customer-safe projection. */
const MISSING_PROJECTION_ERROR =
  "That quote's data could not be loaded. The canvas is unchanged.";

/** The binding modes, in the order the radio group lists them. */
const BINDING_OPTIONS: readonly {
  value: SampleBinding["kind"];
  label: string;
  hint: string;
}[] = [
  {
    value: "placeholders",
    label: "Stubs",
    hint: "Every bound value shows as a chip naming the field it will pull.",
  },
  {
    value: "sample",
    label: "Sample data",
    hint: "See the layout with real-looking text. Your quote settings and organization details are used where you have set them; the rest is fake and editable.",
  },
  {
    value: "quote",
    label: "A real quote",
    hint: "Preview the template with one of your quotes.",
  },
];

interface SampleDataSheetProps {
  /** The template being edited. */
  templateId: string;
  /** The template's current document, scanned for the fields it binds. */
  documentHtml: string;
  /** Whether the sheet is open. */
  open: boolean;
  /** Opens and closes the sheet. */
  onOpenChange: (open: boolean) => void;
  /** Whether the canvas is showing the chrome-free customer view. */
  isCustomerView: boolean;
  /** Switches the canvas between the editor and the customer view. */
  onCustomerViewChange: (customerView: boolean) => void;
}

/**
 * The slide-over driving what the template canvas binds: stub chips,
 * hand-edited sample data, or a real quote's values. The canvas re-renders
 * behind it as values are typed, and clicking the canvas dismisses it.
 * @param templateId - The template being edited
 * @param documentHtml - The template's current document
 * @param open - Whether the sheet is open
 * @param onOpenChange - Opens and closes the sheet
 * @param isCustomerView - Whether the canvas is showing the customer view
 * @param onCustomerViewChange - Switches the canvas between editor and customer view
 * @returns The sample data sheet element
 */
export function SampleDataSheet({
  templateId,
  documentHtml,
  open,
  onOpenChange,
  isCustomerView,
  onCustomerViewChange,
}: SampleDataSheetProps) {
  const binding = useTemplateSampleStore((state) =>
    selectBinding(state.bindings, templateId),
  );
  const sample = useTemplateSampleStore((state) =>
    selectSample(state.samples, templateId),
  );
  const setBinding = useTemplateSampleStore((state) => state.setBinding);
  const setSampleField = useTemplateSampleStore(
    (state) => state.setSampleField,
  );
  const setSampleLineValue = useTemplateSampleStore(
    (state) => state.setSampleLineValue,
  );
  const addSampleLine = useTemplateSampleStore((state) => state.addSampleLine);
  const removeSampleLine = useTemplateSampleStore(
    (state) => state.removeSampleLine,
  );
  const resetSample = useTemplateSampleStore((state) => state.resetSample);
  const configured = useConfiguredSampleValues();

  const getQuote = useRecordQuotesStore((state) => state.getQuote);
  const selectedQuoteId = binding.kind === "quote" ? binding.quoteId : null;
  const loadingState = useRecordQuotesStore((state) =>
    selectedQuoteId ? state.quoteLoadingStates.get(selectedQuoteId) : undefined,
  );
  const hasProjection = useRecordQuotesStore((state) =>
    selectedQuoteId ? state.publicQuotes.has(selectedQuoteId) : false,
  );

  const used = useMemo(() => collectUsedSources(documentHtml), [documentHtml]);

  const pickQuote = (quoteId: string | null) => {
    if (!quoteId) {
      setBinding(templateId, { kind: "placeholders" });
      return;
    }
    setBinding(templateId, { kind: "quote", quoteId });
    getQuote(quoteId, { includePublicQuote: true });
  };

  const changeMode = (kind: string) => {
    if (kind === "quote") {
      setBinding(templateId, { kind: "quote", quoteId: selectedQuoteId ?? "" });
      return;
    }
    setBinding(templateId, {
      kind: kind === "sample" ? "sample" : "placeholders",
    });
  };

  const isLoadingQuote = loadingState?.isLoadingGet ?? false;
  const quoteError =
    loadingState?.getError ??
    (selectedQuoteId && !isLoadingQuote && !hasProjection
      ? MISSING_PROJECTION_ERROR
      : null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-4 sm:max-w-md">
        <SheetHeader className="shrink-0">
          <SheetTitle>Sample data</SheetTitle>
          <SheetDescription>
            Controls what the canvas shows while you author. Nothing here is
            saved into the template.
          </SheetDescription>
        </SheetHeader>

        {/* One scroll container: flex children shrink to fit by default, so a
            scrollable flex column would compress its content instead of
            overflowing. min-h-0 lets this pane actually give up height. The
            negative margins pull it out to the sheet's edges so its scrollbar
            sits flush, and the matching padding puts the content back where it
            was - scrolling forces overflow-x to clip, so without that padding
            a focused field's ring would be cut off at the pane's edge. */}
        <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6">
          <RadioGroup value={binding.kind} onValueChange={changeMode}>
            {BINDING_OPTIONS.map((option) => (
              <div key={option.value} className="flex items-start gap-2">
                <RadioGroupItem
                  id={`sample-mode-${option.value}`}
                  value={option.value}
                  className="mt-1"
                />
                <div className="space-y-0.5">
                  <Label
                    htmlFor={`sample-mode-${option.value}`}
                    className="font-normal leading-none"
                  >
                    {option.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{option.hint}</p>
                </div>
              </div>
            ))}
          </RadioGroup>

          <div className="flex items-start gap-2 border-t pt-4">
            <Checkbox
              id="sample-customer-view"
              checked={isCustomerView}
              onCheckedChange={(checked) =>
                onCustomerViewChange(checked === true)
              }
              className="mt-1"
            />
            <div className="space-y-0.5">
              <Label
                htmlFor="sample-customer-view"
                className="font-normal leading-none"
              >
                Show as customer sees it
              </Label>
              <p className="text-xs text-muted-foreground">
                Hides the editing chrome and paginates the document, so you can
                check the printed layout. Esc returns to editing.
              </p>
            </div>
          </div>

          {binding.kind === "quote" && (
            <div className="space-y-1.5 border-t pt-4">
              <div className="flex items-center gap-2">
                <Label>Quote</Label>
                {isLoadingQuote && (
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <QuoteSelector
                currentlySelectedId={
                  selectedQuoteId === "" ? null : selectedQuoteId
                }
                onSelectId={pickQuote}
              />
              {quoteError && (
                <p className="text-xs text-destructive">{quoteError}</p>
              )}
            </div>
          )}

          {binding.kind === "sample" && (
            <div className="space-y-6 border-t pt-4">
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resetSample(templateId)}
                >
                  Reset defaults
                </Button>
              </div>
              <SampleFieldsForm
                quoteSources={used.quoteSources}
                fields={sample.fields}
                configured={configured}
                lines={sample.lines}
                onFieldChange={(key, value) =>
                  setSampleField(templateId, key, value)
                }
              />
              <SampleLinesEditor
                lineSources={used.lineSources}
                lines={sample.lines}
                onLineChange={(lineId, source, value) =>
                  setSampleLineValue(templateId, lineId, source, value)
                }
                onAddLine={() => addSampleLine(templateId)}
                onRemoveLine={(lineId) => removeSampleLine(templateId, lineId)}
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
